import { useCallback, useEffect, useMemo, useState, useContext, useRef } from 'react';
import { graphql, Link } from 'gatsby';
import { globalHistory } from '@reach/router';
import KontentImage from '@/components/KontentImage/KontentImage';
import MenuItem from './components/MenuItem/MenuItem';
import InformationStripe from './components/InformationStripe/InformationStripe';
import CtaSection from './components/CtaSection/CtaSection';
import HamburgerButton from './components/HamburgerButton/HamburgerButton';
import SearchNavButton from '@/components/Search/components/SearchNavButton/SearchNavButton';
import useHeaderHeight from '@/hooks/useHeaderHeight';
import useInternalLinks from '@/hooks/useInternalLinks';
import useMenuClickOutside from '@/hooks/useMenuClickOutside';
import { type BlockHTMLId } from '@/utils/renderBlock';
import { GeneralContext } from '@/context/GeneralContext';
import { MenuContext } from './contexts/MenuContext';
import {
  ButtonsWrapper,
  StyledMenuItems,
  StyledContainer,
  StyledCtaWrapper,
  StyledHeader,
  StyledLogoWrapper,
  StyledOverlay,
  StyledWrapper,
  StyledMenuLink,
  StyledMenuItemWrapper,
  StyledMobileCTASectionWrapper,
} from './Header.styles';

export interface HeaderProps extends Queries.HEADERFragment {
  layout?: Queries.PAGE_LAYOUTFragment['page_layout__header'];
  hasCTAs?: boolean;
  isSticky: boolean;
}

const Header = ({
  hasCTAs,
  headerElements: elements,
  isSticky,
  layout,
  system,
  id,
}: HeaderProps & BlockHTMLId) => {
  const getInternalLinkURLByCodename = useInternalLinks();
  const { logo, menu_items, cta_section, information_stripe } = elements;
  const { url, description } = logo.value;

  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [activeMenuItemId, setActiveMenuItemId] = useState<string | null>(null);
  const { isInformationStripeVisible, handleHideInformationStripe } = useContext(GeneralContext);
  const { headerHeight } = useHeaderHeight();
  const menuButtonsRef = useRef<Set<HTMLElement>>(new Set());
  const dropdownContentsRef = useRef<Set<HTMLElement>>(new Set());
  const ctaSection = useMemo(
    () => <CtaSection ctas={cta_section?.value} ctaIsOnNavBar />,
    [cta_section?.value]
  );
  const ctaSectionNotInNavBar = useMemo(
    () => <CtaSection ctas={cta_section?.value} ctaIsOnNavBar={false} />,
    [cta_section?.value]
  );

  const hasInformationStripe =
    typeof layout?.value !== 'string' &&
    typeof information_stripe?.value?.elements.title.value === 'string' &&
    isInformationStripeVisible;
  const isMinified = layout?.value === 'minified';
  const homepagePath = getInternalLinkURLByCodename('homepage') ?? '/';
  const isMenuActive = () => activeMenuItemId !== null;

  const toggleMobileNav = useCallback(() => {
    if (isMobileNavOpen) {
      document.documentElement.classList.remove('u-scroll-forbid');
      document.body.classList.remove('u-scroll-forbid');
    } else {
      document.documentElement.classList.add('u-scroll-forbid');
      document.body.classList.add('u-scroll-forbid');
    }
    setIsMobileNavOpen((prevState) => !prevState);
  }, [isMobileNavOpen]);

  const clearActiveMenuItem = useCallback(() => {
    setActiveMenuItemId(null);
  }, []);

  useMenuClickOutside(activeMenuItemId, setActiveMenuItemId, menuButtonsRef, dropdownContentsRef);

  const menuContext = useMemo(
    () => ({
      activeMenuItemId,
      setActiveMenuItemId,
      menuButtonsRef,
      dropdownContentsRef,
    }),
    [activeMenuItemId, setActiveMenuItemId, menuButtonsRef, dropdownContentsRef]
  );

  useEffect(() => {
    const unsubscribe = globalHistory.listen(({ action }) => {
      if (action === 'PUSH' && isMobileNavOpen) {
        toggleMobileNav();
      }
    });

    return unsubscribe;
  }, [isMobileNavOpen, toggleMobileNav]);

  return layout?.value === 'hidden' ? null : (
    <MenuContext.Provider value={menuContext}>
      <StyledWrapper isSticky={isSticky} id={id} data-codename={system.codename}>
        {hasInformationStripe && (
          <InformationStripe onClose={handleHideInformationStripe} {...information_stripe.value} />
        )}
        <StyledHeader data-block-header>
          <StyledContainer>
            <StyledLogoWrapper>
              <Link to={homepagePath} title="Home">
                <KontentImage
                  src={url}
                  alt={description ?? `Sonarsource Logo`}
                  sizes={[{ width: 128, height: 32, quality: 80 }]}
                />
              </Link>
            </StyledLogoWrapper>
            <StyledMenuItems isMobileNavOpen={isMobileNavOpen} offset={headerHeight.toString()}>
              {!isMinified &&
                menu_items.value.map((category) => (
                  <StyledMenuItemWrapper key={category.system.id}>
                    {category.__typename === 'kontent_item_header_menu_item' ? (
                      <MenuItem
                        {...category}
                        onLinkClick={clearActiveMenuItem}
                        ctaSection={ctaSectionNotInNavBar}
                      />
                    ) : (
                      <StyledMenuLink {...category} />
                    )}
                  </StyledMenuItemWrapper>
                ))}
              <StyledMobileCTASectionWrapper>{ctaSectionNotInNavBar}</StyledMobileCTASectionWrapper>
            </StyledMenuItems>
            <ButtonsWrapper>
              <SearchNavButton />
              {hasCTAs && <StyledCtaWrapper>{ctaSection}</StyledCtaWrapper>}
              <HamburgerButton handleOnClick={toggleMobileNav} isActive={isMobileNavOpen} />
            </ButtonsWrapper>
          </StyledContainer>
        </StyledHeader>
      </StyledWrapper>
      <StyledOverlay
        data-testid="overlay"
        isMenuActive={isMenuActive()}
        onClick={() => {
          setIsMobileNavOpen(false);
          setActiveMenuItemId(null);
        }}
      />
    </MenuContext.Provider>
  );
};

export const fragment = graphql`
  fragment HEADER on kontent_item_header {
    system {
      codename
      type
      id
    }
    headerElements: elements {
      ...COMPONENT_A_B_TEST
      information_stripe {
        value {
          ...HEADER_INFORMATION_STRIPE
        }
      }
      logo {
        value {
          url
          description
        }
      }
      menu_items {
        value {
          __typename
          ... on kontent_item {
            system {
              id
            }
          }
          ...HEADER_MENU_ITEM
          ...LINK
        }
      }
      cta_section {
        value {
          __typename
          ... on kontent_item {
            system {
              id
            }
          }
          ...LINK
        }
      }
    }
  }
`;

export default Header;
